import { useEffect, useRef, useState } from 'react';

const MIN_HEIGHT = 240;

/**
 * Max height for a scroll area inside the ref'd element so it fits between its top edge and the
 * viewport bottom, minus `bottomGutter` and any chrome between the element and its scroll area
 * (e.g. a pagination footer). Re-measures on resize and element size changes; never below 240px.
 * `availableHeight` is a CSS pixel string, undefined until the first measurement.
 */
export const useAvailableHeight = <E extends HTMLElement = HTMLDivElement>(
  bottomGutter = 0,
  scrollAreaSelector = '.MuiTableContainer-root'
) => {
  const [element, setElement] = useState<E | null>(null);
  const [availableHeight, setAvailableHeight] = useState<string>();
  const appliedHeight = useRef<number>();

  useEffect(() => {
    if (!element) {
      return;
    }

    const measure = () => {
      const wrapperRect = element.getBoundingClientRect();
      const scrollAreaRect = element.querySelector(scrollAreaSelector)?.getBoundingClientRect();
      const chromeHeight = scrollAreaRect ? Math.max(wrapperRect.height - scrollAreaRect.height, 0) : 0;
      const height = Math.max(
        window.innerHeight - Math.max(wrapperRect.top, 0) - bottomGutter - chromeHeight,
        MIN_HEIGHT
      );
      if (height !== appliedHeight.current) {
        appliedHeight.current = height;
        setAvailableHeight(`${height}px`);
      }
    };

    measure();
    window.addEventListener('resize', measure);

    // Re-measure on element size changes; the result is idempotent, so it settles immediately.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
    observer?.observe(element);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [element, bottomGutter, scrollAreaSelector]);

  return { ref: setElement, availableHeight };
};
